import React, { RefObject, useCallback, useMemo } from 'react';
import { FabricObject } from 'fabric';
import { Clip, Track } from '../../types';
import { colors, dimensions, TimelineMode } from '../../../theme';
import { usePieceDispatch, usePieceSelector } from '../../../model/piece-context';
import type { Layer } from '../../../model/types';
import { usePlayback, playheadStore } from '../../../playback';
import { DragMode, useClipDrag } from '../../hooks/use-clip-drag';
import { usePlayheadScrub } from '../../hooks/use-playhead-scrub';
import { msToPx, SnapTarget } from '../../utils/snap';
import {
  AttachmentGraph,
  buildAttachmentGraph,
  buildTextVideoMap,
  getConnectedClipIds,
  TimedClipEntry,
  TimelineClipEntry
} from '../../utils/attachment-graph';
import { filterGroupableEntries, LaneRegistry, partitionTimelineRows, resolveLaneKey } from '../../utils/timeline-rows';
import { TimelineRuler } from './timeline-ruler';
import { TrackRow } from './track-row';
import { TimelinePlayhead } from './timeline-playhead';

const TRACK_START_PADDING = 12;
const TRACK_END_PADDING = 32;

export type TrackEntry = TimelineClipEntry;

export type TrackRowClipEntry = {
  track: Track;
  clip: Clip;
  fabricObject: FabricObject | undefined;
  displayLabel?: string;
  renderClip?: Clip;
  previewClipId?: string;
  // false para video/texto del grupo inicial (relacionados entre sí): no se puede cambiar su duración.
  resizable?: boolean;
};

type TrackRowEntry = {
  track: Track;
  clips: TrackRowClipEntry[];
  isSelected: boolean;
};

type TimedEntry = TimedClipEntry;
type TextEntry = TimedClipEntry;
type TextGroupEntry = {
  videoClipId: string;
  videoEntry: TrackEntry | undefined;
  primaryText: TextEntry;
  groupTextEntries: TextEntry[];
};

const readLayerText = (layer: Layer | undefined): string | undefined => {
  if (layer?.kind !== 'text' && layer?.kind !== 'pill' && layer?.kind !== 'sobreimpreso') return undefined;
  const text = layer.content.text.trim();
  return text || undefined;
};

type ClipRange = { start: number; end: number };

/** Identifies the drag "lane" a timed entry belongs to: the shared video/text group row, or its own standalone row. */
type RowKeyOf = (entry: TimedEntry) => string;

/** Same-row clip ranges that `id` shouldn't collide with (everything sharing that lane, minus the ones moving with it). */
const sameRowStatics = (
  entries: TrackEntry[],
  rowKeyOf: RowKeyOf,
  rowKey: string,
  excludeIds: Set<string>
): ClipRange[] =>
  entries
    .filter(
      (entry): entry is TimedEntry =>
        !!entry.clip && !excludeIds.has(entry.clip.id) && rowKeyOf(entry as TimedEntry) === rowKey
    )
    .map(entry => ({ start: entry.clip.start, end: entry.clip.start + entry.clip.duration }));

/**
 * Cascades forward from `cursorEnd`: any same-row clip starting at or after `afterPoint`
 * (excluding `excludeIds`) that starts before the cursor gets pushed to start exactly there —
 * dragging its own attached clips (e.g. a video's subtitles) along by the same amount — then the
 * cursor advances past it so the next clip in line is checked the same way. `afterPoint` keeps
 * clips that sit *before* the insertion point (like the neighbor being inserted after) untouched.
 * Returns the new `start` for every clip that had to move.
 */
const computeRipplePushes = (
  entries: TrackEntry[],
  attachmentGraph: AttachmentGraph,
  clipByLayerId: Map<string, Clip>,
  rowKeyOf: RowKeyOf,
  rowKey: string,
  excludeIds: Set<string>,
  afterPoint: number,
  cursorEnd: number
): Map<string, number> => {
  const newStarts = new Map<string, number>();
  const handled = new Set<string>(excludeIds);

  const chain = entries
    .filter(
      (entry): entry is TimedEntry =>
        !!entry.clip &&
        entry.clip.start >= afterPoint &&
        !handled.has(entry.clip.id) &&
        rowKeyOf(entry as TimedEntry) === rowKey
    )
    .sort((a, b) => a.clip.start - b.clip.start);

  let cursor = cursorEnd;
  for (const entry of chain) {
    if (handled.has(entry.clip.id)) continue;
    if (entry.clip.start >= cursor) break; // ya hay lugar; lo que sigue, más a la derecha, también lo tiene

    const pushDelta = cursor - entry.clip.start;
    getConnectedClipIds(attachmentGraph, entry.clip.id).forEach(id => {
      if (handled.has(id)) return;
      handled.add(id);
      const clip = clipByLayerId.get(id);
      if (clip) newStarts.set(id, clip.start + pushDelta);
    });

    cursor += entry.clip.duration;
  }

  return newStarts;
};

type TrackAreaProps = {
  entries: TrackEntry[];
  // Mapa clip id -> lane, ver comentario en timeline-rows.ts. Compartido con LayerColumn para que
  // el agrupamiento de filas coincida siempre, fila a fila.
  laneRegistry: LaneRegistry;
  selectedObject: FabricObject | null;
  totalDuration: number;
  trackWidth: number;
  mode: TimelineMode;
  outerRef: RefObject<HTMLDivElement | null>;
  scrollRef: RefObject<HTMLDivElement | null>;
  onScroll: () => void;
  onSelectClip: (entry: TrackEntry | undefined) => void;
  onDuplicate: (entry: TrackEntry) => void;
  onDelete: (entry: TrackEntry) => void;
};

export const TrackArea: React.FC<TrackAreaProps> = ({
  entries,
  laneRegistry,
  selectedObject,
  totalDuration,
  trackWidth,
  mode,
  outerRef,
  scrollRef,
  onScroll,
  onSelectClip,
  onDuplicate,
  onDelete
}) => {
  const td = dimensions.timeline[mode];
  const usableWidth = trackWidth - TRACK_START_PADDING - TRACK_END_PADDING;
  const pxPerSecond = usableWidth > 0 ? usableWidth / (totalDuration / 1000) : 0;

  const dispatch = usePieceDispatch();
  const layers = usePieceSelector(model => model.layers);
  const { pause } = usePlayback();

  // Lane de arrastre/colisión de cada clip: los de video/texto del grupo inicial comparten una
  // sola lane por kind (como si fueran una fila continua); todo lo demás — clips nuevos, sean
  // del kind que sean — vive en su propia lane individual, así que nunca colisiona ni empuja a
  // clips de otras track-rows.
  const rowKeyOf = useCallback((entry: TimedEntry): string => resolveLaneKey(entry, laneRegistry), [laneRegistry]);

  const clipByLayerId = useMemo(() => {
    const map = new Map<string, Clip>();
    for (const entry of entries) if (entry.clip) map.set(entry.clip.id, entry.clip);
    return map;
  }, [entries]);

  // El attachment graph solo se construye sobre los clips del grupo compartido de video/texto:
  // un track solo (nuevo o duplicado) queda fuera del grafo, o sea sin conexiones
  // (getConnectedClipIds le devuelve únicamente él mismo).
  const groupableEntries = useMemo(() => filterGroupableEntries(entries, laneRegistry), [entries, laneRegistry]);

  const attachmentGraph = useMemo(() => buildAttachmentGraph(groupableEntries), [groupableEntries]);
  const textVideoByClipId = useMemo(() => buildTextVideoMap(groupableEntries), [groupableEntries]);
  const textLabelByClipId = useMemo(() => {
    const labels = new Map<string, string>();
    for (const entry of entries) {
      if (!entry.clip || entry.track.kind !== 'text') continue;
      const text = readLayerText(layers[entry.clip.id]);
      if (text) labels.set(entry.clip.id, text);
    }
    return labels;
  }, [entries, layers]);
  const selectedClipIds = useMemo(
    () => (selectedObject?.layerId ? getConnectedClipIds(attachmentGraph, selectedObject.layerId) : null),
    [attachmentGraph, selectedObject]
  );
  const entryByClipId = useMemo(() => {
    const map = new Map<string, TrackEntry>();
    for (const entry of entries) {
      if (entry.clip) map.set(entry.clip.id, entry);
    }
    return map;
  }, [entries]);

  const rows = useMemo<TrackRowEntry[]>(() => {
    const buckets = partitionTimelineRows(entries, laneRegistry);

    return buckets.map((bucket): TrackRowEntry => {
      if (bucket.kind === 'video-group') {
        const videoEntries = bucket.entries as TimedEntry[];
        return {
          track: videoEntries[0].track,
          clips: videoEntries.map(videoEntry => ({
            track: videoEntry.track,
            clip: videoEntry.clip,
            fabricObject: videoEntry.fabricObject,
            displayLabel: videoEntry.track.label,
            resizable: false
          })),
          isSelected: !!selectedClipIds && videoEntries.some(entry => selectedClipIds.has(entry.clip.id))
        };
      }

      if (bucket.kind === 'text-group') {
        const textEntries = bucket.entries as TextEntry[];
        const textGroupsByVideoId = new Map<string, TextEntry[]>();
        const orphanTextEntries: TextEntry[] = [];

        for (const textEntry of textEntries) {
          const videoClipId = textVideoByClipId.get(textEntry.clip.id);
          if (!videoClipId) {
            orphanTextEntries.push(textEntry);
            continue;
          }
          const group = textGroupsByVideoId.get(videoClipId);
          if (group) group.push(textEntry);
          else textGroupsByVideoId.set(videoClipId, [textEntry]);
        }

        const textGroupEntries: TextGroupEntry[] = Array.from(textGroupsByVideoId.entries())
          .map(([videoClipId, groupTextEntries]: [string, TextEntry[]]) => {
            const videoEntry = entryByClipId.get(videoClipId);
            const primaryText: TextEntry = groupTextEntries[0];
            return { videoClipId, videoEntry, primaryText, groupTextEntries };
          })
          .sort((a, b) => a.primaryText.clip.start - b.primaryText.clip.start);

        const groupedClips: TrackRowClipEntry[] = textGroupEntries.map(group => {
          const videoClip = group.videoEntry?.clip;
          const renderClip = videoClip ?? group.primaryText.clip;
          const subtitleText = group.groupTextEntries
            .map(textEntry => textLabelByClipId.get(textEntry.clip.id))
            .filter((text): text is string => !!text)
            .join(' · ');
          return {
            track: group.primaryText.track,
            clip: group.primaryText.clip,
            fabricObject: group.primaryText.fabricObject,
            displayLabel: subtitleText || group.primaryText.track.label,
            renderClip,
            previewClipId: renderClip.id,
            resizable: false
          };
        });

        const orphanClips: TrackRowClipEntry[] = orphanTextEntries.map(textEntry => ({
          track: textEntry.track,
          clip: textEntry.clip,
          fabricObject: textEntry.fabricObject,
          displayLabel: textLabelByClipId.get(textEntry.clip.id) ?? textEntry.track.label,
          resizable: false
        }));

        return {
          track: textEntries[0].track,
          clips: [...groupedClips, ...orphanClips],
          isSelected: !!selectedClipIds && textEntries.some(entry => selectedClipIds.has(entry.clip.id))
        };
      }

      // 'solo': una lane propia (track nuevo, o uno solo con sus duplicados). Puede tener más de
      // un clip una vez que el original se duplicó — todos se listan en la misma fila.
      const soloEntries = bucket.entries;
      const first = soloEntries[0];
      if (soloEntries.length === 1 && !first.clip) {
        return { track: first.track, clips: [], isSelected: false };
      }

      const clips: TrackRowClipEntry[] = soloEntries
        .filter((entry): entry is TimedEntry => !!entry.clip)
        .map(entry => ({
          track: entry.track,
          clip: entry.clip,
          fabricObject: entry.fabricObject,
          displayLabel:
            entry.track.kind === 'text'
              ? (textLabelByClipId.get(entry.clip.id) ?? entry.track.label)
              : entry.track.label
        }));

      return {
        track: first.track,
        clips,
        isSelected: !!selectedClipIds && soloEntries.some(entry => !!entry.clip && selectedClipIds.has(entry.clip.id))
      };
    });
  }, [entries, selectedClipIds, textVideoByClipId, textLabelByClipId, entryByClipId, laneRegistry]);

  const { startScrub } = usePlayheadScrub({
    pxPerSecond,
    startOffset: TRACK_START_PADDING,
    maxTime: totalDuration,
    containerRef: outerRef,
    onScrubStart: pause // pausar el playback al empezar a arrastrar / clickear el ruler
  });

  // Static snap targets: composition bounds + every clip's edges.
  const staticSnapTargets = useMemo<SnapTarget[]>(() => {
    const targets: SnapTarget[] = [
      { px: TRACK_START_PADDING, kind: 'composition-start' },
      { px: msToPx(totalDuration, pxPerSecond, TRACK_START_PADDING), kind: 'composition-end' }
    ];
    for (const entry of entries) {
      if (!entry.clip) continue;
      const leftPx = msToPx(entry.clip.start, pxPerSecond, TRACK_START_PADDING);
      const rightPx = msToPx(entry.clip.start + entry.clip.duration, pxPerSecond, TRACK_START_PADDING);
      targets.push({ px: leftPx, clipId: entry.clip.id, kind: 'clip-start' });
      targets.push({ px: rightPx, clipId: entry.clip.id, kind: 'clip-end' });
    }
    return targets;
  }, [entries, pxPerSecond, totalDuration]);

  const getSnapTargets = useCallback((): SnapTarget[] => {
    const playheadPx = msToPx(playheadStore.get(), pxPerSecond, TRACK_START_PADDING);
    return [...staticSnapTargets, { px: playheadPx, kind: 'playhead' }];
  }, [staticSnapTargets, pxPerSecond]);

  const handleDragCommit = useCallback(
    (commit: { clipId: string; mode: DragMode; newStartMs?: number; newDurationMs?: number }) => {
      const connectedClipIds = getConnectedClipIds(attachmentGraph, commit.clipId);
      const layerId = commit.clipId; // entries del modelo: clip.id = layerId
      switch (commit.mode) {
        case 'move': {
          if (commit.newStartMs === undefined) return;
          const sourceClip = clipByLayerId.get(layerId);
          if (!sourceClip) return;
          const sourceEntry = entryByClipId.get(layerId);
          const sourceRowKey = sourceEntry ? rowKeyOf(sourceEntry as TimedEntry) : undefined;

          const movers = Array.from(connectedClipIds)
            .map(id => {
              const clip = clipByLayerId.get(id);
              const entry = entryByClipId.get(id);
              return clip && entry ? { id, clip, rowKey: rowKeyOf(entry as TimedEntry) } : null;
            })
            .filter((m): m is { id: string; clip: Clip; rowKey: string } => !!m);

          // El movimiento nunca se rechaza: el clip siempre se ubica en cualquier parte del
          // timeline. Si el destino cae dentro de otro clip fijo de su misma lane, se corre
          // justo después de su final (en vez de superponerse) y se empuja en cascada lo que
          // haga falta al resto de la cadena — igual que antes, pero para cualquier colisión,
          // no solo cuando el destino "engancha" el final de un vecino.
          let desiredStart = Math.max(0, sourceClip.start + (commit.newStartMs - sourceClip.start));
          if (sourceRowKey) {
            const hit = sameRowStatics(entries, rowKeyOf, sourceRowKey, connectedClipIds).find(
              range => desiredStart >= range.start && desiredStart < range.end
            );
            if (hit) desiredStart = hit.end;
          }

          const insertDelta = desiredStart - sourceClip.start;
          const finalStarts = new Map<string, number>();
          movers.forEach(mover => {
            finalStarts.set(mover.id, Math.max(0, mover.clip.start + insertDelta));
          });

          // Los clips conectados (p.ej. un video y sus subtítulos) se mueven todos por el mismo
          // delta, pero cada lane que tocan resuelve sus propias colisiones por separado — así
          // el track de subtítulos no queda limitado por lo que le pasa al de video, ni viceversa.
          const moversByRowKey = new Map<string, { id: string; clip: Clip }[]>();
          movers.forEach(mover => {
            const list = moversByRowKey.get(mover.rowKey);
            if (list) list.push(mover);
            else moversByRowKey.set(mover.rowKey, [mover]);
          });

          moversByRowKey.forEach((laneMovers, rowKey) => {
            const excludeIds = new Set(laneMovers.map(mover => mover.id));
            const anchor = laneMovers.reduce((min, mover) =>
              (finalStarts.get(mover.id) ?? mover.clip.start) < (finalStarts.get(min.id) ?? min.clip.start)
                ? mover
                : min
            );
            const anchorStart = finalStarts.get(anchor.id) ?? anchor.clip.start;
            const cursorEnd = anchorStart + anchor.clip.duration;

            const ripplePushes = computeRipplePushes(
              entries,
              attachmentGraph,
              clipByLayerId,
              rowKeyOf,
              rowKey,
              excludeIds,
              anchorStart,
              cursorEnd
            );
            ripplePushes.forEach((newStart, id) => {
              if (!finalStarts.has(id)) finalStarts.set(id, newStart);
            });
          });

          finalStarts.forEach((start, id) => {
            dispatch({ type: 'setLayerTiming', id, start });
          });
          return;
        }
        case 'resize-end': {
          if (commit.newDurationMs === undefined) return;
          const sourceClip = clipByLayerId.get(layerId);
          const sourceEntry = entryByClipId.get(layerId);
          const rowKey = sourceEntry ? rowKeyOf(sourceEntry as TimedEntry) : undefined;
          let newDuration = commit.newDurationMs;

          if (sourceClip && rowKey) {
            const nextStart = sameRowStatics(entries, rowKeyOf, rowKey, new Set([layerId]))
              .filter(range => range.start > sourceClip.start)
              .reduce((min, range) => Math.min(min, range.start), Infinity);
            if (Number.isFinite(nextStart)) newDuration = Math.min(newDuration, nextStart - sourceClip.start);
          }
          dispatch({ type: 'setLayerTiming', id: layerId, duration: newDuration, fit: 'fixed' });
          return;
        }
        case 'resize-start': {
          if (commit.newDurationMs === undefined) return;
          const sourceClip = clipByLayerId.get(layerId);
          if (!sourceClip) return;
          const sourceEntry = entryByClipId.get(layerId);
          const rowKey = sourceEntry ? rowKeyOf(sourceEntry as TimedEntry) : undefined;

          let start = Math.max(0, sourceClip.start + (sourceClip.duration - commit.newDurationMs));
          if (rowKey) {
            const prevEnd = sameRowStatics(entries, rowKeyOf, rowKey, new Set([layerId]))
              .filter(range => range.end <= sourceClip.start)
              .reduce((max, range) => Math.max(max, range.end), 0);
            start = Math.max(start, prevEnd);
          }

          const duration = sourceClip.start + sourceClip.duration - start;
          if (duration <= 0) return;

          dispatch({
            type: 'setLayerTiming',
            id: layerId,
            duration,
            fit: 'fixed',
            start
          });
          const delta = start - sourceClip.start;
          connectedClipIds.forEach(id => {
            if (id === layerId) return;
            const linkedClip = clipByLayerId.get(id);
            if (!linkedClip) return;
            dispatch({ type: 'setLayerTiming', id, start: Math.max(0, linkedClip.start + delta) });
          });

          if (sourceClip.kind === 'audio') {
            const prevTrimIn = sourceClip.trim?.in ?? 0;
            dispatch({
              type: 'patchAudioContent',
              id: layerId,
              patch: {
                trim: {
                  in: Math.max(0, prevTrimIn + delta),
                  out: sourceClip.trim?.out ?? 0
                }
              }
            });
          }
          return;
        }
      }
    },
    [attachmentGraph, clipByLayerId, dispatch, entries, entryByClipId, rowKeyOf]
  );

  const { snapshot, startDrag } = useClipDrag({
    pxPerSecond,
    startOffset: TRACK_START_PADDING,
    onCommit: handleDragCommit,
    getSnapTargets
  });

  const linkedClipIds = useMemo(
    () => (snapshot ? getConnectedClipIds(attachmentGraph, snapshot.clipId) : null),
    [attachmentGraph, snapshot]
  );

  const handleClipDragStart = useCallback(
    (e: React.MouseEvent, clip: Clip, dragMode: DragMode) => {
      startDrag(e, clip.id, clip.start, clip.duration, dragMode);
    },
    [startDrag]
  );

  return (
    <div
      ref={outerRef}
      style={{
        flex: 1,
        minWidth: 0,
        backgroundColor: colors.trackAreaBg,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      <TimelineRuler
        totalDuration={totalDuration}
        pxPerSecond={pxPerSecond}
        trackWidth={usableWidth}
        startOffset={TRACK_START_PADDING}
        onScrubStart={startScrub}
      />

      <div
        ref={scrollRef}
        onScroll={onScroll}
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          gap: td.rowGap,
          padding: `${td.rowGap}px 0`
        }}
      >
        {rows.map(row => {
          // console.log('Track: ', row);
          return (
            <TrackRow
              key={row.track.id}
              track={row.track}
              clips={row.clips}
              selectedClipIds={selectedClipIds}
              mode={mode}
              isSelected={row.isSelected}
              pxPerSecond={pxPerSecond}
              startOffset={TRACK_START_PADDING}
              dragSnapshot={snapshot}
              dragLinkedClipIds={linkedClipIds}
              onSelect={() => {
                const firstClip = row.clips[0];
                if (!firstClip) {
                  onSelectClip(undefined);
                  return;
                }
                const selectedClipId =
                  firstClip.track.kind === 'text' ? textVideoByClipId.get(firstClip.clip.id) : firstClip.clip.id;
                const selectedEntry = selectedClipId ? entryByClipId.get(selectedClipId) : undefined;
                onSelectClip(selectedEntry ?? firstClip);
              }}
              onClipSelect={entry => {
                const selectedClipId =
                  entry.track.kind === 'text' ? textVideoByClipId.get(entry.clip.id) : entry.clip.id;
                const selectedEntry = selectedClipId ? entryByClipId.get(selectedClipId) : undefined;
                onSelectClip(selectedEntry ?? entry);
              }}
              onClipDuplicate={onDuplicate}
              onClipDelete={onDelete}
              onClipDragStart={handleClipDragStart}
            />
          );
        })}
      </div>

      {snapshot?.snapPx !== null && snapshot?.snapPx !== undefined && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: snapshot.snapPx,
            width: 1,
            backgroundColor: colors.accent,
            pointerEvents: 'none',
            zIndex: 4,
            opacity: 0.7
          }}
        />
      )}

      {trackWidth > 0 && (
        <TimelinePlayhead pxPerSecond={pxPerSecond} startOffset={TRACK_START_PADDING} onScrubStart={startScrub} />
      )}
    </div>
  );
};
